/**
 * Pronto MoQ steering player.
 *
 * Connects to the relay, discovers truck broadcasts by announcement (mirroring
 * moq-boy / moq-pilot discovery), renders each as a video tile, and lets you
 * take control of one truck with the keyboard. The {@link Truck} backend
 * publishes a render-only `control` track and subscribes to the truck's
 * `status` track for round-trip confirmation.
 *
 * @module
 */

import * as Moq from "@moq/net";
import { drawGuide } from "./guides.ts";
import { applyKeys, changed, REST } from "./input.ts";
import type { DriveState, Gear } from "./schemas.ts";
import { nextGear, shiftGear, Truck } from "./truck.ts";
import {
	applyWheel,
	arbitrate,
	Calibration,
	failSafe,
	type InputSource,
	shortName,
	Wheel,
	type WheelSample,
} from "./wheel.ts";

// Baked at build time; the JWT arrives in the URL fragment so the hosting
// server and referrer headers never receive it.
// Fallback matches the justfile's default `relay_url` and localhost.toml's
// anonymous-everything auth root, for running `vite` outside of `just web`.
const BASE_RELAY = import.meta.env.VITE_MOQ_RELAY ?? "http://localhost:4443";

/** Build the relay URL from the baked base and a one-time `#jwt=` fragment. */
function buildRelayUrl(): URL {
	const base = new URL(BASE_RELAY);
	const jwt = new URLSearchParams(window.location.hash.slice(1)).get("jwt");
	if (jwt) {
		base.searchParams.set("jwt", jwt);
		history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
	}
	return base;
}

const connection = new Moq.Connection.Reload({ enabled: true, url: buildRelayUrl() });
const driving = new Moq.Signals.Signal<string | undefined>(undefined);
const signals = new Moq.Signals.Effect();

const els = {
	conn: document.getElementById("conn") as HTMLSpanElement,
	empty: document.getElementById("empty") as HTMLDivElement,
	trucks: document.getElementById("trucks") as HTMLDivElement,
	wheel: document.getElementById("wheel") as HTMLButtonElement,
	cal: document.getElementById("wheel-cal") as HTMLDivElement,
};

// --- Connection status indicator. ---
signals.run((effect) => {
	const state = effect.get(connection.status);
	els.conn.textContent = state;
	els.conn.dataset.state = state;
});

// --- Discovery: maintain a Truck per announced ".hang" broadcast. ---
const tiles = new Map<string, TruckTile>();

signals.run((effect) => {
	const conn = effect.get(connection.established);
	if (!conn) return;

	const announced = conn.announced();
	effect.spawn(async () => {
		for (;;) {
			const entry = await Promise.race([effect.cancel, announced.next()]);
			if (!entry) break;

			// Trucks publish at "{truck}/{camera}.hang". Ignore any other ".hang"
			// broadcast that may share a multi-tenant relay - otherwise an
			// unrelated broadcast becomes a bogus truck tile whose unreadable
			// status track would spin the UI.
			if (!isTruckBroadcast(entry.path)) continue;

			if (entry.active && !tiles.has(entry.path)) {
				const truck = new Truck({ broadcastId: entry.path, connection, driving });
				tiles.set(entry.path, new TruckTile(truck));
				refreshEmpty();
			} else if (!entry.active) {
				tiles.get(entry.path)?.dispose();
				tiles.delete(entry.path);
				if (driving.peek() && !hasTruck(driving.peek())) driving.set(undefined);
				refreshEmpty();
			}
		}
	});
});

// Trucks are published as "{truck}/{camera}.hang" - exactly one path segment
// before the camera name. This deliberately excludes bare "foo.hang" and deeper
// "a/b/c.hang" broadcasts that might coexist on a shared relay.
function isTruckBroadcast(path: string): boolean {
	return /^[^/]+\/[^/]+\.hang$/.test(path);
}

function hasTruck(id: string | undefined): boolean {
	for (const tile of tiles.values()) if (tile.truck.id === id) return true;
	return false;
}

function refreshEmpty() {
	els.empty.hidden = tiles.size > 0;
}

// --- Keyboard steering for the active truck. ---
const held = new Set<string>();
const KEY = (e: KeyboardEvent) => e.key.toLowerCase();
const DRIVE_KEYS = ["w", "a", "s", "d", "c", "arrowup", "arrowdown", "arrowleft", "arrowright"];

window.addEventListener("keydown", (e) => {
	// The calibration dialog owns the driving keys while it's up, so that
	// working the pedals to bind them can't also steer. E-stop is the exception:
	// an unavailable e-stop is a hazard, while a spurious one is an annoyance,
	// so Space keeps working here and the dialog carries its own button.
	if (calibration) {
		if (e.key === "Escape") closeCalibration();
		if (KEY(e) === " ") activeTruck()?.estop();
		e.preventDefault();
		return;
	}
	const k = KEY(e);
	if (k === " ") {
		activeTruck()?.estop();
		e.preventDefault();
		return;
	}
	if (k === "g" && !e.repeat) {
		const t = activeTruck();
		if (t) t.setDrive({ gear: nextGear(t.drive.peek().gear as Gear) });
		e.preventDefault();
		return;
	}
	if (k === "r" && !e.repeat) {
		activeTruck()?.resetEstop();
		e.preventDefault();
		return;
	}
	if (DRIVE_KEYS.includes(k)) {
		held.add(k);
		// Reaching for the keyboard takes the axes back from the wheel, which
		// otherwise holds them at wherever the rim and pedals happen to sit.
		source.set("keyboard");
		e.preventDefault();
	}
});
window.addEventListener("keyup", (e) => held.delete(KEY(e)));
window.addEventListener("blur", () => held.clear());

function activeTruck(): Truck | undefined {
	const id = driving.peek();
	for (const tile of tiles.values()) if (tile.truck.id === id) return tile.truck;
	return undefined;
}

// --- Steering wheel (Gamepad API). ---
//
// Only one source drives the axes at a time. The keyboard holds them by
// default; a connected wheel takes over once it actually moves, and only with
// the throttle released, so a pedal resting under a foot can never be what
// hands it control. Any drive key takes the axes straight back.
const wheel = new Wheel();
const source = new Moq.Signals.Signal<InputSource>("keyboard");

/**
 * Advance the arbitration, failing the truck safe if an armed wheel vanished.
 *
 * Unplugging mid-drive must not silently become a held keyboard command: the
 * driver has lost the device that was setting throttle, so cut it here rather
 * than letting {@link applyKeys} hold it. Fires once on the transition, so the
 * keyboard is free to drive normally afterwards.
 */
function updateSource(sample: WheelSample | undefined) {
	const { source: next, lost } = arbitrate(source.peek(), sample);
	source.set(next);
	if (!lost) return;
	// A key held from before the wheel armed is still in `held`, and applyKeys
	// runs later this same frame - without this it would start easing the brake
	// back off the instant the fail-safe applied it.
	held.clear();
	const truck = activeTruck();
	if (truck && !truck.estopped.peek()) truck.setDrive(failSafe(truck.drive.peek()));
}

function wheelButtons(sample: WheelSample) {
	const truck = activeTruck();
	// E-stop is the documented exception to arbitration: any source may latch it
	// at any time. Gear is a drive command, so it follows the active source -
	// a bumped paddle must not shift a truck being driven from the keyboard.
	if (sample.pressed.has("estop")) truck?.estop();
	if (!truck || truck.estopped.peek() || source.peek() !== "wheel") return;
	const gear = truck.drive.peek().gear as Gear;
	if (sample.pressed.has("gearUp")) truck.setDrive({ gear: shiftGear(gear, 1) });
	if (sample.pressed.has("gearDown")) truck.setDrive({ gear: shiftGear(gear, -1) });
}

// Integrate the active input source into the drive state ~60 Hz. The interval
// fallback keeps input (and therefore the control heartbeat's payload)
// advancing when rAF is paused, e.g. in a hidden or embedded tab.
let lastFrame = performance.now();

function step() {
	const now = performance.now();
	const dt = Math.min((now - lastFrame) / 1000, 0.1);
	lastFrame = now;

	const sample = wheel.poll();
	refreshWheel();

	// Calibration reads the raw device, so nothing it sees should reach a truck.
	if (calibration) {
		const pad = wheel.pad();
		if (pad) calibration.update(pad);
		refreshCalibration();
		return;
	}

	// Arbitrate before reading the buttons: the gear gate below consults the
	// active source, which must already reflect this frame's sample.
	updateSource(sample);
	if (sample) wheelButtons(sample);

	const truck = activeTruck();
	if (truck && !truck.estopped.peek()) {
		const cur = truck.drive.peek();
		const next = source.peek() === "wheel" && sample ? applyWheel(cur, sample, dt) : applyKeys(cur, held, dt);
		if (changed(cur, next)) truck.setDrive(next);
	}
}

function tick() {
	step();
	requestAnimationFrame(tick);
}
requestAnimationFrame(tick);
setInterval(step, 250);

// --- Wheel status chip + calibration dialog. ---
let calibration: Calibration | undefined;
let lastWheelLabel = "";

const calEls = {
	device: els.cal.querySelector(".cal-device") as HTMLDivElement,
	progress: els.cal.querySelector(".cal-progress") as HTMLDivElement,
	prompt: els.cal.querySelector(".cal-prompt") as HTMLParagraphElement,
	live: els.cal.querySelector(".cal-live") as HTMLDivElement,
	warn: els.cal.querySelector(".cal-warn") as HTMLDivElement,
	capture: els.cal.querySelector(".cal-capture") as HTMLButtonElement,
	skip: els.cal.querySelector(".cal-skip") as HTMLButtonElement,
	cancel: els.cal.querySelector(".cal-cancel") as HTMLButtonElement,
	estop: els.cal.querySelector(".cal-estop") as HTMLButtonElement,
};

els.wheel.addEventListener("click", () => openCalibration());
// The modal covers the tile's e-stop, so it has to carry its own.
calEls.estop.addEventListener("click", () => activeTruck()?.emergencyStop());
calEls.cancel.addEventListener("click", () => closeCalibration());
calEls.skip.addEventListener("click", () => {
	calibration?.skip();
	finishCalibration();
});
calEls.capture.addEventListener("click", () => {
	// A failed capture means the control didn't move far enough to tell apart
	// from the rest snapshot; leave the step up and say so.
	if (calibration && !calibration.capture()) {
		calEls.warn.hidden = false;
		return;
	}
	calEls.warn.hidden = true;
	finishCalibration();
});

/** Reflect the connected wheel in the header chip. */
function refreshWheel() {
	const id = wheel.id;
	if (!id) {
		els.wheel.hidden = true;
		lastWheelLabel = "";
		return;
	}
	const label = `${shortName(id)}${wheel.calibrated ? "" : " · uncalibrated"}`;
	if (label !== lastWheelLabel) {
		els.wheel.textContent = label;
		els.wheel.title = `${id} - click to calibrate`;
		lastWheelLabel = label;
	}
	els.wheel.hidden = false;
	els.wheel.dataset.driving = String(source.peek() === "wheel");
}

function openCalibration() {
	if (!wheel.pad()) return;
	calibration = new Calibration();
	// The wheel has to re-arm afterwards, since the axes it reports now mean
	// something different than they did a moment ago.
	source.set("keyboard");
	held.clear();
	// Calibration means working the pedals for reasons that have nothing to do
	// with driving, and the dialog suppresses the normal control loop, so park
	// the truck before it goes up rather than leaving whatever was commanded to
	// keep heartbeating out for the duration.
	activeTruck()?.setDrive({ ...REST });
	calEls.warn.hidden = true;
	els.cal.hidden = false;
	refreshCalibration();
}

function closeCalibration() {
	calibration = undefined;
	els.cal.hidden = true;
}

/** Save and close once the last step is bound, otherwise redraw the dialog. */
function finishCalibration() {
	if (!calibration) return;
	if (!calibration.done) {
		refreshCalibration();
		return;
	}
	const profile = calibration.result();
	if (profile) wheel.save(profile);
	closeCalibration();
}

function refreshCalibration() {
	const cal = calibration;
	if (!cal) return;
	if (cal.done) {
		finishCalibration();
		return;
	}

	const step = cal.step;
	if (!step) return;

	calEls.device.textContent = wheel.id ? shortName(wheel.id) : "wheel disconnected";
	calEls.progress.textContent = `step ${cal.index + 1} of ${cal.total}`;
	calEls.prompt.textContent = step.prompt;
	// Button steps bind on the press itself - there is no free hand for Capture.
	calEls.capture.hidden = step.kind === "button";
	calEls.skip.hidden = !step.optional;

	const pad = wheel.pad();
	calEls.live.textContent = pad
		? pad.axes.map((value, index) => `a${index} ${value.toFixed(2)}`).join("  ")
		: "no signal from the wheel";
}

// --- A rendered tile bound to one Truck. ---
class TruckTile {
	readonly truck: Truck;
	readonly root: HTMLDivElement;
	readonly #signals = new Moq.Signals.Effect();
	#overlay!: HTMLCanvasElement;

	constructor(truck: Truck) {
		this.truck = truck;
		this.root = document.createElement("div");
		this.root.className = "truck";
		this.root.innerHTML = `
			<div class="video-wrap"><span class="badge">live</span><canvas></canvas><canvas class="overlay"></canvas></div>
			<div class="body">
				<div class="name-row"><div class="name"></div><button class="estop-btn" type="button" title="Emergency stop - anyone can trigger, lease or not">E-STOP</button></div>
				<div class="legend">
					<span><i class="input"></i>your input</span>
					<span><i class="accepted"></i>truck accepted</span>
				</div>
				<div class="gauges">
					${gauge("steering", "center")}${gauge("throttle")}
					${gauge("brake")}<div class="gauge"><span class="label">gear</span><span class="gear-val"></span></div>
				</div>
				<div class="telemetry">
					<div class="row"><span>truck accepted</span><span class="echo"></span></div>
					<div class="row"><span>mode</span><span class="mode"></span></div>
					<div class="row"><span>control round trip</span><span class="rtt"></span></div>
					<div class="row"><span>media pipeline</span><span class="lat"></span></div>
				</div>
				<div class="hint"></div>
			</div>`;
		els.trucks.appendChild(this.root);

		const canvas = this.root.querySelector("canvas:not(.overlay)") as HTMLCanvasElement;
		truck.setCanvas(canvas);
		this.#overlay = this.root.querySelector("canvas.overlay") as HTMLCanvasElement;

		this.root.addEventListener("click", () => {
			driving.set(driving.peek() === truck.id ? undefined : truck.id);
		});

		const name = this.root.querySelector(".name");
		const estop = this.root.querySelector(".estop-btn");
		if (!name || !estop) throw new Error("truck tile template is missing required controls");
		name.textContent = truck.id;

		// E-stop is a safety control anyone may trigger, so it lives on every tile
		// and works whether or not you hold the lease. stopPropagation keeps the
		// click from also toggling this tile's control request.
		estop.addEventListener("click", (e) => {
			e.stopPropagation();
			this.truck.emergencyStop();
		});

		this.#bind();
	}

	#bind() {
		const q = (s: string) => this.root.querySelector(s) as HTMLElement;

		// Active highlight + hint.
		this.#signals.run((effect) => {
			const active = effect.get(this.truck.active);
			this.root.dataset.active = String(active);
			const status = effect.get(this.truck.status);
			// Latched if either side thinks so: the local signal reacts
			// instantly, the status echo is authoritative (see resetEstop).
			const estopped = effect.get(this.truck.estopped) || Boolean(status?.estopped);
			const driverId = effect.get(this.truck.driverId);
			let hint = "click to request control";
			if (active && estopped) {
				hint = "e-stop latched - press R to reset into full brake";
			} else if (active && (!driverId || !status?.driver)) {
				hint = "requesting the control lease...";
			} else if (active && status?.driver !== driverId) {
				hint = `control held by ${status?.driver}`;
			} else if (active && effect.get(source) === "wheel") {
				hint = "you hold the lease - steering by wheel · paddles shift · press a key for keyboard";
			} else if (active) {
				hint = "you hold the lease - A/D steer · C center · W/S throttle/brake · G gear · Space e-stop";
			}
			q(".hint").textContent = hint;
		});

		// Our requested drive state → gauges.
		this.#signals.run((effect) => {
			const d = effect.get(this.truck.drive);
			setBar(q(".gauge-steering .bar > span"), (d.steering + 1) / 2);
			setBar(q(".gauge-throttle .bar > span"), d.throttle);
			setBar(q(".gauge-brake .bar > span"), d.brake);
			q(".gear-val").textContent = d.gear;
		});

		// Truck telemetry echo.
		this.#signals.run((effect) => {
			const s = effect.get(this.truck.status);
			if (!s) {
				q(".echo").textContent = "-";
				q(".mode").textContent = "-";
				q(".rtt").textContent = "-";
				q(".lat").textContent = "-";
				return;
			}
			q(".echo").textContent = fmtDrive(s.accepted);
			const mode = q(".mode");
			mode.textContent = s.estopped ? `${s.mode} · E-STOPPED` : s.mode;
			mode.className = `mode ${s.estopped ? "estopped" : "render-only"}`;
			const me = effect.get(this.truck.driverId);
			const mine = me ? s.media_pipeline[me] : undefined;
			const roundTrip = effect.get(this.truck.roundTripMs);
			q(".rtt").textContent = roundTrip == null ? "-" : `${Math.round(roundTrip)}ms`;
			q(".lat").textContent = mine?.length ? mine.map((l) => `${l.label} ${l.ms}ms`).join(" · ") : "-";
		});

		// Overlay: only your **requested input** (cyan, dashed) is drawn
		// client-side, while you hold control. Everything the truck actually
		// accepted - the amber rails, distance rungs, and gear tag - is
		// rendered truck-side and burned into the video, so the simulation
		// visuals are authoritative, not a JS reconstruction. Divergence
		// between cyan and amber is control delay or clamping.
		this.#signals.run((effect) => {
			const input = effect.get(this.truck.drive);
			const active = effect.get(this.truck.active);
			this.#drawOverlay(input, active);
		});

		// Repaint on layout/resize (the initial effect run happens before layout,
		// so the overlay has zero size until the first observation fires).
		const ro = new ResizeObserver(() => this.#drawOverlay(this.truck.drive.peek(), this.truck.active.peek()));
		ro.observe(this.#overlay);
		this.#signals.cleanup(() => ro.disconnect());
	}

	#drawOverlay(input: DriveState, active: boolean) {
		const canvas = this.#overlay;
		const cssW = canvas.clientWidth;
		const cssH = canvas.clientHeight;
		if (cssW === 0 || cssH === 0) return;

		const dpr = window.devicePixelRatio || 1;
		canvas.width = Math.round(cssW * dpr);
		canvas.height = Math.round(cssH * dpr);
		const ctx = canvas.getContext("2d");
		if (!ctx) return;
		ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
		ctx.clearRect(0, 0, cssW, cssH);

		// Your requested input, shown while you hold control so you can see lead/lag.
		if (active) {
			drawGuide(ctx, cssW, cssH, input, {
				color: "#50c8ff",
				width: 2,
				dash: [7, 6],
				alpha: 0.9,
			});
		}
	}

	dispose() {
		this.#signals.close();
		this.truck.close();
		this.root.remove();
	}
}

function gauge(name: string, extra = ""): string {
	return `<div class="gauge gauge-${name}"><span class="label">${name}</span><div class="bar ${extra}"><span></span></div></div>`;
}

function setBar(el: HTMLElement, frac: number) {
	const pct = Math.round(Math.max(0, Math.min(1, frac)) * 100);
	if (el.parentElement?.classList.contains("center")) {
		// Center-anchored bar for steering (50% = straight).
		const mid = 50;
		el.style.left = `${Math.min(mid, pct)}%`;
		el.style.width = `${Math.abs(pct - mid)}%`;
	} else {
		el.style.left = "0";
		el.style.width = `${pct}%`;
	}
}

function fmtDrive(d: DriveState): string {
	return `s${d.steering >= 0 ? "+" : ""}${d.steering.toFixed(2)} t${d.throttle.toFixed(2)} b${d.brake.toFixed(2)} ${d.gear}`;
}
